import Button from "@material-ui/core/Button"
import ButtonGroup from "@material-ui/core/ButtonGroup"
import Checkbox from "@material-ui/core/Checkbox"
import FormControlLabel from "@material-ui/core/FormControlLabel"
import InputLabel from "@material-ui/core/InputLabel"
import OutlinedInput from "@material-ui/core/OutlinedInput"
import Paper from "@material-ui/core/Paper"
import Select from "@material-ui/core/Select"
import Typography from "@material-ui/core/Typography"
import { type BillingSettingsFormValues, useBillingSettingsForm } from "./use-billing-settings-form"
import { makeStyles, useTheme } from "@material-ui/core"
import { getBillingCountryOptions, getBillingLanguageOptions } from "./billing-form-options"
import { Check } from "react-feather"
import { useTranslation } from "react-i18next"
import { Alert } from "@material-ui/lab"
import { useAuthUser } from "../../../users/hooks/use-auth-user"
import format from "date-fns/format"

const billingCountryOptions = getBillingCountryOptions()
const billingLanguageOptions = getBillingLanguageOptions()

type BillingSettingsFormProps = {
  orgId: string
  billingSettings: BillingSettingsFormValues
  policyAgreedAt: string | null
  policyAgreedBy: string | null
  onAfterSave?: () => void
}

export function BillingSettingsForm({
  orgId,
  billingSettings,
  policyAgreedAt,
  policyAgreedBy,
  onAfterSave,
}: BillingSettingsFormProps) {
  const { dateFormat } = useAuthUser()
  const classes = useStyles()
  const theme = useTheme()
  const { t } = useTranslation("org")
  const {
    formState,
    isAgreeToPolicyDisabled,
    meta,
    autoFillContactPerson,
    resetContactPersonAutoFill,
    isContactPersonAutoFilled,
    handleContactPersonFieldChange,
    autoFillInvoicingEmail,
    isInvoicingEmailAutoFilled,
    setIsInvoicingEmailAutoFilled,
    handleInvoicingEmailChange,
    hasAllRequiredFields,
    setInvoicingMethod,
  } = useBillingSettingsForm({
    orgId,
    initialValues: billingSettings,
    onSuccess: onAfterSave,
  })

  const { invoicingAddress } = formState.values
  const hasChosenEmailInvoice = invoicingAddress.invoicingMethod === "email"
  const hasChosenEInvoice = invoicingAddress.invoicingMethod === "eInvoice"
  const showPolicyAlert = isAgreeToPolicyDisabled && !formState.values.agreeToPolicy
  const showPolicyCheckbox = !isAgreeToPolicyDisabled && !policyAgreedAt && !policyAgreedBy
  const showPolicyAgreedInfo = policyAgreedAt || policyAgreedBy

  return (
    <Paper variant="outlined" style={{ padding: theme.spacing(3), border: 0 }}>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          formState.handleSubmit(e)
        }}
      >
        <section style={{ marginBottom: 32 }}>
          <Typography variant="h5" gutterBottom>
            {t("orgBillingPage.billingAddressHeader")}
          </Typography>
          <Paper variant="outlined" style={{ padding: 24, marginTop: 24 }}>
            <div className={classes.formControl}>
              <InputLabel htmlFor="company">{t("orgBillingPage.companyNameLabel")}</InputLabel>
              <OutlinedInput
                id="company"
                name="billingAddress.company"
                value={formState.values.billingAddress.company}
                onChange={formState.handleChange}
                placeholder={t("orgBillingPage.companyInputPlaceholder")}
                fullWidth
              />
            </div>

            <div className={classes.formControl}>
              <InputLabel htmlFor="street">{t("orgBillingPage.streetLabel")}</InputLabel>
              <OutlinedInput
                id="street"
                name="billingAddress.street"
                value={formState.values.billingAddress.street}
                onChange={formState.handleChange}
                placeholder={t("orgBillingPage.streetInputPlaceholder")}
                fullWidth
              />
            </div>

            <div className={classes.formControl}>
              <InputLabel htmlFor="postalCode">{t("orgBillingPage.postalCodeLabel")}</InputLabel>
              <OutlinedInput
                id="postalCode"
                name="billingAddress.postalCode"
                value={formState.values.billingAddress.postalCode}
                onChange={formState.handleChange}
                placeholder={t("orgBillingPage.postalCodeInputPlaceholder")}
                fullWidth
              />
            </div>

            <div className={classes.formControl}>
              <InputLabel htmlFor="city">{t("orgBillingPage.cityLabel")}</InputLabel>
              <OutlinedInput
                id="city"
                name="billingAddress.city"
                value={formState.values.billingAddress.city}
                onChange={formState.handleChange}
                placeholder={t("orgBillingPage.cityInputPlaceholder")}
                fullWidth
              />
            </div>

            <div className={classes.formControl}>
              <InputLabel htmlFor="country">{t("orgBillingPage.countryLabel")}</InputLabel>
              <Select
                native
                id="country"
                name="billingAddress.country"
                value={formState.values.billingAddress.country}
                onChange={formState.handleChange}
                input={<OutlinedInput labelWidth={60} name="billingAddress.country" id="country" />}
                fullWidth
              >
                <option aria-label="None" value="">
                  {t("orgBillingPage.selectCountry")}
                </option>
                {billingCountryOptions.map((country) => (
                  <option key={country.code} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </Select>
            </div>
          </Paper>
        </section>
        <section style={{ marginBottom: 32 }}>
          <Typography variant="h5" gutterBottom>
            {t("orgBillingPage.billingContactPersonHeader")}
          </Typography>
          <Paper variant="outlined" style={{ padding: 24, marginTop: 24 }}>
            <FormControlLabel
              className={classes.autoFillContact}
              control={
                <Checkbox
                  size="small"
                  checked={isContactPersonAutoFilled}
                  onChange={(e) => {
                    if (e.target.checked) {
                      autoFillContactPerson()
                    } else {
                      resetContactPersonAutoFill()
                    }
                  }}
                  name="confirmation"
                  color="primary"
                />
              }
              label={t("orgBillingPage.useMyContactDetails")}
            />

            <div className={classes.formControl}>
              <InputLabel htmlFor="contactFirstName">{t("orgBillingPage.firstnameLabel")}</InputLabel>
              <OutlinedInput
                id="contactFirstName"
                name="contactPerson.firstname"
                value={formState.values.contactPerson.firstname}
                onChange={handleContactPersonFieldChange}
                placeholder={t("orgBillingPage.firstnameInputPlaceholder")}
                fullWidth
              />
            </div>
            <div className={classes.formControl}>
              <InputLabel htmlFor="contactLastName">{t("orgBillingPage.lastnameLabel")}</InputLabel>
              <OutlinedInput
                id="contactLastName"
                name="contactPerson.lastname"
                value={formState.values.contactPerson.lastname}
                onChange={handleContactPersonFieldChange}
                placeholder={t("orgBillingPage.lastnameInputPlaceholder")}
                fullWidth
              />
            </div>
            <div className={classes.formControl}>
              <InputLabel htmlFor="contactEmail">{t("orgBillingPage.emailLabel")}</InputLabel>
              <OutlinedInput
                id="contactEmail"
                name="contactPerson.email"
                value={formState.values.contactPerson.email}
                onChange={handleContactPersonFieldChange}
                placeholder={t("orgBillingPage.emailInputPlaceholder")}
                fullWidth
              />
            </div>
            <div className={classes.formControl}>
              <InputLabel htmlFor="contactPhone">
                {t("orgBillingPage.phoneLabel")} ({t("orgBillingPage.optional")})
              </InputLabel>
              <OutlinedInput
                id="contactPhone"
                name="contactPerson.phone"
                value={formState.values.contactPerson.phone}
                onChange={handleContactPersonFieldChange}
                placeholder={t("orgBillingPage.phoneInputPlaceholder")}
                fullWidth
              />
            </div>
            <div className={classes.formControl}>
              <InputLabel htmlFor="contactLanguage">
                {t("orgBillingPage.languageLabel")} ({t("orgBillingPage.optional")})
              </InputLabel>
              <Select
                native
                id="contactLanguage"
                name="contactPerson.language"
                value={formState.values.contactPerson.language}
                onChange={handleContactPersonFieldChange}
                input={<OutlinedInput labelWidth={70} name="contactPerson.language" id="contactLanguage" />}
                fullWidth
              >
                <option aria-label="None" value="">
                  {t("orgBillingPage.selectLanguage")}
                </option>
                {billingLanguageOptions.map((lang) => (
                  <option key={lang.code} value={lang.value}>
                    {lang.label}
                  </option>
                ))}
              </Select>
            </div>
          </Paper>
        </section>
        <section>
          <Typography variant="h5" gutterBottom>
            {t("orgBillingPage.invoicingAddressHeader")}
          </Typography>
          <Paper variant="outlined" style={{ padding: 24, marginTop: 24 }}>
            <div className={classes.formControl}>
              <ButtonGroup size="large" fullWidth>
                <Button
                  variant={hasChosenEmailInvoice ? "contained" : "outlined"}
                  startIcon={hasChosenEmailInvoice ? <Check size={18} /> : null}
                  onClick={() => setInvoicingMethod("email")}
                  style={{
                    color: hasChosenEmailInvoice ? theme.palette.common.white : theme.palette.text.primary,
                    backgroundColor: hasChosenEmailInvoice ? theme.palette.primary.dark : "transparent",
                    outlineColor: hasChosenEmailInvoice ? theme.palette.text.secondary : "transparent",
                  }}
                >
                  {t("orgBillingPage.emailInvoiceMethodLabel")}
                </Button>
                <Button
                  variant={hasChosenEInvoice ? "contained" : "outlined"}
                  startIcon={hasChosenEInvoice ? <Check size={18} /> : null}
                  onClick={() => setInvoicingMethod("eInvoice")}
                  style={{
                    color: hasChosenEInvoice ? theme.palette.common.white : theme.palette.text.primary,
                    backgroundColor: hasChosenEInvoice ? theme.palette.primary.dark : "transparent",
                    outlineColor: hasChosenEInvoice ? theme.palette.text.secondary : "transparent",
                  }}
                >
                  {t("orgBillingPage.eInvoiceMethodLabel")}
                </Button>
              </ButtonGroup>
            </div>
            {hasChosenEmailInvoice ? (
              <div className={classes.formControl} style={{ marginTop: 36, marginBottom: 36 }}>
                <InputLabel htmlFor="invoicingEmail">{t("orgBillingPage.invoicingEmailLabel")}</InputLabel>
                <OutlinedInput
                  id="invoicingEmail"
                  name="invoicingAddress.invoicingEmail"
                  value={invoicingAddress.invoicingEmail}
                  onChange={handleInvoicingEmailChange}
                  placeholder={t("orgBillingPage.invoicingEmailPlaceholder")}
                  fullWidth
                />
                <FormControlLabel
                  label={t("orgBillingPage.useMyEmailAsInvoicingEmail")}
                  className={classes.autoFillInvoiceEmail}
                  control={
                    <Checkbox
                      size="small"
                      checked={isInvoicingEmailAutoFilled}
                      onChange={(e) => {
                        if (e.target.checked) {
                          autoFillInvoicingEmail()
                        } else {
                          formState.setFieldValue("invoicingAddress.invoicingEmail", "")
                          setIsInvoicingEmailAutoFilled(false)
                        }
                      }}
                      name="confirmation"
                      color="primary"
                    />
                  }
                />
              </div>
            ) : null}
            {hasChosenEInvoice ? (
              <>
                <div className={classes.formControl}>
                  <InputLabel htmlFor="electronicInvoicingAddress">
                    {t("orgBillingPage.electronicInvoicingAddressLabel")}
                  </InputLabel>
                  <OutlinedInput
                    id="electronicInvoicingAddress"
                    name="invoicingAddress.electronicInvoicingAddress"
                    value={invoicingAddress.electronicInvoicingAddress}
                    onChange={formState.handleChange}
                    placeholder={t("orgBillingPage.electronicInvoicingAddressPlaceholder")}
                    fullWidth
                  />
                </div>
                <div className={classes.formControl}>
                  <InputLabel htmlFor="invoicingOperatorId">{t("orgBillingPage.invoicingOperatorIdLabel")}</InputLabel>
                  <OutlinedInput
                    id="invoicingOperatorId"
                    name="invoicingAddress.invoicingOperatorId"
                    value={invoicingAddress.invoicingOperatorId}
                    onChange={formState.handleChange}
                    placeholder={t("orgBillingPage.invoicingOperatorIdPlaceholder")}
                    fullWidth
                  />
                </div>
              </>
            ) : null}
            <div className={classes.formControl}>
              <InputLabel htmlFor="vatNumber">{t("orgBillingPage.vatNumberLabel")}</InputLabel>
              <OutlinedInput
                id="vatNumber"
                name="invoicingAddress.vatNumber"
                value={invoicingAddress.vatNumber}
                onChange={formState.handleChange}
                placeholder={t("orgBillingPage.vatNumberPlaceholder")}
                fullWidth
              />
            </div>
            <div className={classes.formControl}>
              <InputLabel htmlFor="invoicingReferenceNumber">
                {t("orgBillingPage.invoicingReferenceNumberLabel")} ({t("orgBillingPage.optional")})
              </InputLabel>
              <OutlinedInput
                id="invoicingReferenceNumber"
                name="invoicingAddress.invoicingReferenceNumber"
                value={invoicingAddress.invoicingReferenceNumber}
                onChange={formState.handleChange}
                placeholder={t("orgBillingPage.invoicingReferenceNumberPlaceholder")}
                fullWidth
              />
            </div>
          </Paper>
        </section>
        <section>
          <div
            style={{
              marginTop: 24,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 16,
            }}
          >
            {showPolicyAlert ? (
              <Alert variant="filled" severity="error">
                {t("orgBillingPage.provideInvoicingDetailsAlert")}
              </Alert>
            ) : null}

            {showPolicyCheckbox ? (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formState.values.agreeToPolicy}
                    onChange={(e) => formState.setFieldValue("agreeToPolicy", e.target.checked)}
                    name="confirmation"
                    color="primary"
                    disabled={isAgreeToPolicyDisabled}
                  />
                }
                label={t("orgBillingPage.agreeToPolicyText")}
              />
            ) : null}

            {showPolicyAgreedInfo ? (
              <Alert
                icon={
                  <Check
                    size={24}
                    style={{
                      color: theme.palette.common.white,
                      backgroundColor: theme.palette.success.main,
                      borderRadius: "50%",
                      padding: 3,
                    }}
                  />
                }
                severity="success"
                style={{ width: "100%" }}
              >
                <Typography variant="subtitle2">
                  {t("orgBillingPage.policyAgreedText", {
                    date: policyAgreedAt ? format(new Date(policyAgreedAt), dateFormat) : "",
                    user: policyAgreedBy || "",
                  })}
                </Typography>
              </Alert>
            ) : null}

            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={meta.isLoading || !hasAllRequiredFields || !formState.values.agreeToPolicy}
            >
              {t("orgBillingPage.saveButtonLabel")}
            </Button>
          </div>
        </section>
      </form>
    </Paper>
  )
}

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginBottom: theme.spacing(2.5),
    marginTop: theme.spacing(2.5),
  },
  autoFillInvoiceEmail: {
    marginLeft: 0,
    "& .MuiSvgIcon-root": {
      fontSize: 16,
    },
    "& .MuiFormControlLabel-label": {
      fontSize: theme.typography.body2.fontSize,
    },
  },
  autoFillContact: {
    border: `1px solid ${theme.palette.divider}`,
    width: "100%",
    marginLeft: 0,
    borderRadius: 4,
    padding: theme.spacing(1),
  },
}))
